import React from 'react';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedProps {
  /** Title of the access denied message */
  title?: string;
  /** Main message content */
  message?: string | React.ReactNode;
  /** Small description or instruction text */
  description?: string;
  /** Text for the action button */
  buttonText?: string;
  /** Link to navigate to on button click (if onButtonClick is not provided) */
  buttonLink?: string;
  /** Callback function for button click */
  onButtonClick?: () => void;
  /** Optional custom icon */
  icon?: React.ReactNode;
  /** Tailwind classes for the icon background container */
  iconBgClassName?: string;
  /** Tailwind classes for the icon itself (if using default icon) */
  iconClassName?: string;
  /** Tailwind classes for the action button */
  buttonClassName?: string;
  /** Tailwind classes for the root container */
  className?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({
  title = 'Access Restricted',
  message,
  description = 'If you believe this is an error, please contact the system administrator.',
  buttonText = 'Return to Home',
  buttonLink = '/',
  onButtonClick,
  icon,
  iconBgClassName = 'bg-gray-300',
  iconClassName = 'text-gray-600',
  buttonClassName = 'bg-black text-white hover:bg-gray-800',
  className = 'flex items-center justify-center min-h-[400px]',
}) => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      navigate(buttonLink);
    }
  };

  return (
    <div className={className}>
      <div className="text-center max-w-md mx-auto">
        <div
          className={`w-16 h-16 ${iconBgClassName} rounded-full flex items-center justify-center mx-auto mb-6`}
        >
          {icon || (
            <svg
              className={`w-8 h-8 ${iconClassName}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          )}
        </div>

        <h3 className="text-xl font-baijamjuree font-bold text-gray-900 mb-3">
          {title}
        </h3>

        {message && (
          <div className="text-gray-700 mb-4 font-baijamjuree max-w-md mx-auto">
            {message}
          </div>
        )}

        {description && (
          <p className="text-sm text-gray-500 font-baijamjuree mb-6">
            {description}
          </p>
        )}

        <button
          onClick={handleButtonClick}
          className={`${buttonClassName} px-6 py-2 rounded-full text-sm font-semibold font-baijamjuree uppercase tracking-wide transition-colors`}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default AccessDenied;
